import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonBlockquote,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonHorizontalRule,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuSelectTextAlign,
  RichTextEditor,
  type RichTextEditorRef,
} from 'mui-tiptap';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

type NoticeType = 'success' | 'warning' | 'error';

interface NoticeData {
  message: string;
  type: NoticeType;
  isEnabled: boolean;
  expirationDate: Dayjs | null;
  exists: boolean;
}

const languages = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'pl', name: 'Polski' },
  { code: 'de', name: 'Deutsch' },
];

const NoticeEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const allLanguages = languages;
  
  const [currentTab, setCurrentTab] = useState<string>(allLanguages[0]?.code || 'en');
  const [notices, setNotices] = useState<Record<string, NoticeData>>({});
  const [rteRefs, setRteRefs] = useState<Record<string, React.RefObject<RichTextEditorRef | null>>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Initialize refs for each language
  useEffect(() => {
    const refs: Record<string, React.RefObject<RichTextEditorRef | null>> = {};
    allLanguages.forEach(lang => {
      refs[lang.code] = React.createRef<RichTextEditorRef | null>();
    });
    setRteRefs(refs);
  }, []);

  // Initialize notices state for each language
  useEffect(() => {
    const initialNotices: Record<string, NoticeData> = {};
    allLanguages.forEach(lang => {
      initialNotices[lang.code] = {
        message: '',
        type: 'success',
        isEnabled: false,
        expirationDate: dayjs(),
        exists: false,
      };
    });
    setNotices(initialNotices);
  }, []);

  // Load notice for all languages
  useEffect(() => {
    const loadNotice = async () => {
      try {
        const data = await apiService.getNotice();
        
        if (data) {
          // Map language codes to message fields
          const messageMap: Record<string, string> = {
            en: data.messageEn || '',
            de: data.messageDe || '',
            ru: data.messageRu || '',
            pl: data.messagePl || '',
          };

          const loadedNotices: Record<string, NoticeData> = {};
          
          allLanguages.forEach(lang => {
            loadedNotices[lang.code] = {
              message: messageMap[lang.code] || '',
              type: (data.type || 'success') as NoticeType,
              isEnabled: data.isEnabled || false,
              expirationDate: data.expirationDate ? dayjs(data.expirationDate) : dayjs(),
              exists: true,
            };
            
            // Set content in editor
            if (rteRefs[lang.code]?.current?.editor && messageMap[lang.code]) {
              rteRefs[lang.code]?.current?.editor?.commands.setContent(messageMap[lang.code]);
            }
          });
          
          setNotices(prev => ({ ...prev, ...loadedNotices }));
        } else {
          // Initialize empty notices for all languages
          const emptyNotices: Record<string, NoticeData> = {};
          allLanguages.forEach(lang => {
            emptyNotices[lang.code] = {
              message: '',
              type: 'success',
              isEnabled: false,
              expirationDate: dayjs(),
              exists: false,
            };
          });
          setNotices(prev => ({ ...prev, ...emptyNotices }));
        }
      } catch (error) {
        console.error('Error loading notice:', error);
        // Initialize empty notices on error
        const emptyNotices: Record<string, NoticeData> = {};
        allLanguages.forEach(lang => {
          emptyNotices[lang.code] = {
            message: '',
            type: 'success',
            isEnabled: false,
            expirationDate: dayjs(),
            exists: false,
          };
        });
        setNotices(prev => ({ ...prev, ...emptyNotices }));
      }
    };

    if (Object.keys(rteRefs).length > 0) {
      loadNotice();
    }
  }, [Object.keys(rteRefs).length]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  // Update editor content when tab changes
  useEffect(() => {
    const notice = notices[currentTab];
    if (notice && rteRefs[currentTab]?.current?.editor) {
      // Use setTimeout to ensure editor is ready
      setTimeout(() => {
        if (rteRefs[currentTab]?.current?.editor) {
          rteRefs[currentTab].current.editor.commands.setContent(notice.message || '');
        }
      }, 0);
    }
  }, [currentTab]);

  const handleContentUpdate = (lang: string) => ({ editor }: { editor: any }) => {
    setNotices(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        message: editor.getHTML(),
      },
    }));
  };

  const handleTypeChange = (event: any) => {
    const newType = event.target.value as NoticeType;
    // Update type for all languages (shared field)
    setNotices(prev => {
      const updated: Record<string, NoticeData> = {};
      Object.keys(prev).forEach(langKey => {
        updated[langKey] = {
          ...prev[langKey],
          type: newType,
        };
      });
      return updated;
    });
  };

  const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    // Update isEnabled for all languages (shared field)
    setNotices(prev => {
      const updated: Record<string, NoticeData> = {};
      Object.keys(prev).forEach(langKey => {
        updated[langKey] = {
          ...prev[langKey],
          isEnabled: newEnabled,
        };
      });
      return updated;
    });
  };

  const handleExpirationDateChange = (newValue: Dayjs | null) => {
    // Update expirationDate for all languages (shared field)
    setNotices(prev => {
      const updated: Record<string, NoticeData> = {};
      Object.keys(prev).forEach(langKey => {
        updated[langKey] = {
          ...prev[langKey],
          expirationDate: newValue,
        };
      });
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      const currentNotice = notices[currentTab];
      if (!currentNotice) return;

      const htmlContent = rteRefs[currentTab]?.current?.editor?.getHTML() || '';
      
      const noticeData = {
        message: htmlContent,
        lang: currentTab,
        type: currentNotice.type,
        isEnabled: currentNotice.isEnabled,
        expirationDate: currentNotice.expirationDate ? currentNotice.expirationDate.toISOString() : '',
      };

      if (currentNotice.exists) {
        await apiService.updateNotice(noticeData);
        setNotices(prev => ({
          ...prev,
          [currentTab]: { ...prev[currentTab], exists: true },
        }));
        setSnackbar({
          open: true,
          message: t('noticeEditor.messages.updatedSuccess'),
          severity: 'success',
        });
      } else {
        await apiService.createNotice(noticeData);
        setNotices(prev => ({
          ...prev,
          [currentTab]: { ...prev[currentTab], exists: true },
        }));
        setSnackbar({
          open: true,
          message: t('noticeEditor.messages.createdSuccess'),
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error saving notice:', error);
      setSnackbar({
        open: true,
        message: t('noticeEditor.messages.errorSaving'),
        severity: 'error',
      });
    }
  };

  const currentNotice = notices[currentTab];

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('noticeEditor.title')}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="language tabs"
          >
            {allLanguages.map(lang => (
              <Tab
                key={lang.code}
                label={lang.name}
                value={lang.code}
              />
            ))}
          </Tabs>
        </Box>

        {currentNotice && (
          <>
            <RichTextEditor
              key={currentTab}
              ref={rteRefs[currentTab]}
              extensions={[
                StarterKit,
                Underline,
                TextAlign.configure({
                  types: ['heading', 'paragraph'],
                }),
              ]}
              content={currentNotice.message}
              onUpdate={handleContentUpdate(currentTab)}
              renderControls={() => (
                <MenuControlsContainer>
                  <MenuButtonUndo />
                  <MenuButtonRedo />
                  <MenuDivider />
                  <MenuSelectHeading />
                  <MenuDivider />
                  <MenuButtonBold />
                  <MenuButtonItalic />
                  <MenuButtonUnderline />
                  <MenuButtonStrikethrough />
                  <MenuDivider />
                  <MenuSelectTextAlign />
                  <MenuDivider />
                  <MenuButtonBulletedList />
                  <MenuButtonOrderedList />
                  <MenuDivider />
                  <MenuButtonBlockquote />
                  <MenuButtonCode />
                  <MenuButtonCodeBlock />
                  <MenuDivider />
                  <MenuButtonHorizontalRule />
                </MenuControlsContainer>
              )}
            />
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 2 },
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  flex: { xs: 1, sm: 'none' },
                }}
              >
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>{t('noticeEditor.type')}</InputLabel>
                  <Select
                    value={currentNotice.type}
                    label={t('noticeEditor.type')}
                    onChange={handleTypeChange}
                  >
                    <MenuItem value="success">{t('noticeEditor.types.success')}</MenuItem>
                    <MenuItem value="warning">{t('noticeEditor.types.warning')}</MenuItem>
                    <MenuItem value="error">{t('noticeEditor.types.error')}</MenuItem>
                  </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDateTimePicker
                    label={t('noticeEditor.expirationDate')}
                    onChange={handleExpirationDateChange}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        size: 'medium',
                        inputProps: { 'aria-label': t('noticeEditor.expirationDate') },
                        sx: { minWidth: { xs: '100%', sm: 200 } },
                      },
                    }}
                    value={currentNotice.expirationDate}
                  />
                </LocalizationProvider>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'space-between', sm: 'flex-end' },
                  gap: 2,
                  flex: { xs: 1, sm: 'none' },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currentNotice.isEnabled}
                      onChange={handleEnabledChange}
                      color="primary"
                    />
                  }
                  label={t('noticeEditor.enabled')}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSave}
                  sx={{ minWidth: { xs: 'auto', sm: 120 } }}
                >
                  {t('noticeEditor.saveNotice')}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NoticeEditorPage;
